/**
 * Register link-designation vocabulary.
 *
 * WHAT THIS FILE IS NOW. It is a set of frozen CONSTANTS describing which entity types a register
 * column may be designated as linking to, and which field of that entity a designation may match
 * on. That is authoring metadata: the "Links to" picker writes these exact strings into the
 * register's `SchemaJson` (`RegisterColumnDefinition.LinkEntityType` / `LinkKeyField`), and the
 * SERVER reads them back. Nothing here resolves, paints, or decides anything.
 *
 * WHAT IT USED TO BE, AND WHY THAT LEFT. It was also a registry of four client-side RESOLVERS that
 * turned a register cell into geometry by walking the CML colouring index, the tag services and the
 * documents service — and a `resolvePoiTarget` dispatcher over them. Spatial focus is now resolved
 * server-side and rendered through `useSpatialFocus`
 * (`.codex/features/spatial-focus/README.md` -> "Client spatial-focus composable"), so a second,
 * client-side resolution path would be exactly the duplicated authority Plan C removes: it can
 * disagree with the server about which rows a scope covers, and it collapses occurrences the
 * resolver deliberately preserves. The resolvers, the batched `loadCmlPoiIndex`, the
 * `normalizePoiKey` helper and the dispatcher were therefore deleted rather than wrapped.
 *
 * Consumers: `@/features/Registers/modules/registerLinkOptions` (the designation picker) and
 * `./poiConfig` (the widget-level highlight/context tri-state). Both are AUTHORING surfaces.
 */

/**
 * Frozen entity-type identifiers. CASE-SENSITIVE — these mirror the values the server's
 * `RegisterColumnDefinition.LinkEntityType` stores, and the spatial catalogue echoes back as
 * `spatial.target.kind` / `spatial.context.kind`. `masterTag` is context-only: the whole-tag path
 * already covers it, so it is never offered as a designatable link target.
 */
export const POI_ENTITY_TYPES = Object.freeze({
  CML: 'cml',
  ANOMALY_TAG: 'anomalyTag',
  INSPECTION_TAG: 'inspectionTag',
  DOCUMENT: 'document',
  MASTER_TAG: 'masterTag',
});

/**
 * The default field of the target a register cell is matched against, per entity type. ONE place —
 * field names are the server's (PascalCase), so a register column may override with its own
 * `LinkKeyField` without touching this.
 */
export const DEFAULT_KEY_FIELD = Object.freeze({
  [POI_ENTITY_TYPES.CML]: 'CmlNo',
  [POI_ENTITY_TYPES.ANOMALY_TAG]: 'Name',
  [POI_ENTITY_TYPES.INSPECTION_TAG]: 'Name',
  [POI_ENTITY_TYPES.DOCUMENT]: 'DocumentTitle',
});

/** The two fields a tag designation may match on (`GET_BASE_TAG_BY_ID_NAME` takes either). */
const TAG_KEY_FIELD = Object.freeze({ NAME: 'Name', ID: 'Id' });

/**
 * The entity types a register column may be DESIGNATED as linking to.
 *
 * Kept under its original name because the designation picker enumerates it
 * (`registerLinkOptions.js`: `Object.keys(POI_RESOLVERS)`) and the stored `LinkEntityType` strings
 * must not shift. The values are the type ids themselves rather than functions: resolution is the
 * server's, and leaving four live client resolvers reachable from here is precisely the second
 * authority this cutover removed. `masterTag` stays out, exactly as before.
 */
export const POI_RESOLVERS = Object.freeze({
  [POI_ENTITY_TYPES.CML]: POI_ENTITY_TYPES.CML,
  [POI_ENTITY_TYPES.ANOMALY_TAG]: POI_ENTITY_TYPES.ANOMALY_TAG,
  [POI_ENTITY_TYPES.INSPECTION_TAG]: POI_ENTITY_TYPES.INSPECTION_TAG,
  [POI_ENTITY_TYPES.DOCUMENT]: POI_ENTITY_TYPES.DOCUMENT,
});

/**
 * The `LinkKeyField` values each designation can actually be matched on server-side — the truth a
 * designation UI must offer, so a register can never be declared with a key field that would be
 * declined at resolve time. The per-type default is always first. Tags accept `Name` or `Id`; cml
 * and document support only their own default, because their lookups (`CmlNo` / an exact
 * `DocumentTitle`) cannot match anything else.
 */
export const SUPPORTED_KEY_FIELDS = Object.freeze({
  [POI_ENTITY_TYPES.CML]: Object.freeze([DEFAULT_KEY_FIELD[POI_ENTITY_TYPES.CML]]),
  [POI_ENTITY_TYPES.ANOMALY_TAG]: Object.freeze([TAG_KEY_FIELD.NAME, TAG_KEY_FIELD.ID]),
  [POI_ENTITY_TYPES.INSPECTION_TAG]: Object.freeze([TAG_KEY_FIELD.NAME, TAG_KEY_FIELD.ID]),
  [POI_ENTITY_TYPES.DOCUMENT]: Object.freeze([DEFAULT_KEY_FIELD[POI_ENTITY_TYPES.DOCUMENT]]),
});
